import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Stack,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorIcon from "@mui/icons-material/Error";
import { createPortfolio } from "../../../api/portfolio_api";
import { uploadPortfolioFile } from "../../../api/stocks_api";

const MAX_NAME = 50;

// Accepted file types
const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const ACCEPTED_ACCEPT = ".xlsx,.xls,.csv";
const ACCEPTED_LABEL = "Excel (.xlsx, .xls) or CSV (.csv)";

const market = sessionStorage.getItem("selectedMarket") ?? "US";

const CreatePortfolio = ({ open, onClose, onCreated }) => {
  const [mode, setMode] = useState(0); // 0 = manual, 1 = import
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [uploadFile, setUploadFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [marketMismatch, setMarketMismatch] = useState(null);

  useEffect(() => {
    if (open) {
      setName("");
      setError("");
      setMode(0);
      setUploadFile(null);
      setFileError("");
      setMarketMismatch(null);
    }
  }, [open]);

  /* ── Unsaved-changes warning (8.1.3) ── */
  useEffect(() => {
    const isDirty = open && (name.trim().length > 0 || uploadFile !== null);
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [open, name, uploadFile]);


  /* ── Manual create ─────────────────────────────────── */
  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) { setError("Portfolio name is required"); return; }
    if (trimmedName.length < 2) { setError("Portfolio name must be at least 2 characters"); return; }

    setLoading(true);
    setError("");
    const res = await createPortfolio({ name: trimmedName, market_code: market, holdings: [] });
    if (res?.success) { onCreated(res.data); setName(""); onClose(); }
    else setError(res?.message || "Failed to create portfolio");
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading && mode === 0) handleCreate();
  };

  /* ── File upload ───────────────────────────────────── */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isValid = ACCEPTED_EXTENSIONS.some(ext => fileName.endsWith(ext));

    if (!isValid) {
      setFileError(`Please upload a valid file: ${ACCEPTED_LABEL}`);
      setUploadFile(null);
      return;
    }
    setUploadFile(file);
    setFileError("");
  };

  const handleImport = async () => {
    if (!uploadFile) {
      setFileError(`Please upload a file: ${ACCEPTED_LABEL}`);
      return;
    }
    setUploading(true);
    setError("");
    setMarketMismatch(null);
    try {
      const uploadRes = await uploadPortfolioFile(uploadFile);

      if (!uploadRes?.success) {
        setError(uploadRes?.response_message || "File upload failed");
        setUploading(false);
        return;
      }

      const respData = uploadRes?.response_data ?? uploadRes?.data?.response_data;
      const uploadedMarket = respData?.market_code ?? null;
      const uploadedMarketName = respData?.market_name ?? uploadedMarket;

      // Market mismatch — keep dialog open and show an info banner
      if (uploadedMarket && uploadedMarket !== market) {
        setMarketMismatch({ market_code: uploadedMarket, market_name: uploadedMarketName });
        onCreated();          // still refresh the portfolio list in the background
        setUploading(false);
        return;               // don't close — let the user read the banner
      }
      onCreated();
      onClose();
    } catch {
      setError("Failed to import portfolio");
    }
    setUploading(false);
  };

  const isValid = name.trim().length >= 2;

  /* ── Shared sx helpers (CSS vars only, no raw values) ─ */
  const cancelBtnSx = {
    textTransform: "none",
    py: 0.65,
    borderRadius: 1,
    color: "var(--color-text-secondary)",
    borderColor: "var(--color-border-primary)",
    "&:hover": { borderColor: "var(--color-border-secondary)", bgcolor: "var(--color-surface-hover)" },
  };

  const primaryBtnSx = {
    textTransform: "none",
    py: 0.65,
    borderRadius: 1,
    bgcolor: "var(--color-primary)",
    boxShadow: "none",
    "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
    "&.Mui-disabled": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" },
  };

  const tabsSx = {
    minHeight: 40,
    "& .MuiTab-root": {
      minHeight: 40,
      textTransform: "none",
      color: "var(--color-text-secondary)",
      "&.Mui-selected": { color: "var(--color-primary)" },
    },
    "& .MuiTabs-indicator": { backgroundColor: "var(--color-primary)", height: 2 },
  };

  const inputSx = {
    borderRadius: 1,
    bgcolor: "var(--color-bg-secondary)",
    color: "var(--color-text-primary)",
    "& input::placeholder": { color: "var(--color-text-secondary)", opacity: 1 },
    "& fieldset": { borderColor: "var(--color-border-primary)" },
    "&:hover fieldset": { borderColor: "var(--color-border-secondary)" },
    "&.Mui-focused fieldset": { borderColor: "var(--color-primary)", borderWidth: "1px" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "var(--color-surface)",
          backgroundImage: "none",
          borderRadius: 1.5,
          border: "1px solid var(--color-border-primary)",
          boxShadow: "var(--shadow-xl)",
          m: { xs: 2, sm: 3 },
          maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 48px)" },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* ── HEADER ── */}
        <Box
          sx={{
            px: 2.5, pt: 1.75, pb: 1.5,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 32, height: 32, borderRadius: 1,
                bgcolor: "var(--color-surface-hover)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <FolderIcon sx={{ color: "var(--color-primary)" }} />
            </Box>
            {/* uses theme.typography.dialogTitle — add to theme.js */}
            <Typography variant="dialogTitle" color="var(--color-text-primary)">
              Create portfolio
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "var(--color-text-secondary)", p: 0.5,
              "&:hover": { bgcolor: "var(--color-surface-hover)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── TABS ── */}
        <Box sx={{ borderBottom: "1px solid var(--color-border-primary)" }}>
          <Tabs value={mode} onChange={(_, v) => setMode(v)} sx={tabsSx}>
            <Tab label="Create empty" tabIndex={0} />
            <Tab label="Import from file" tabIndex={0} />
          </Tabs>
        </Box>

        <Box sx={{ px: 2.5, py: 2.5 }}>

          {/* ════════════════════════════════
              MODE 0 — Manual create
          ════════════════════════════════ */}
          {mode === 0 && (
            <>
              {/* uses theme.typography.body */}
              <Typography variant="body" color="var(--color-text-secondary)" component="p" mb={2}>
                Create a new portfolio to track your investments, monitor performance,
                and analyze your holdings in real-time.
              </Typography>

              <Box>
                {/* uses theme.typography.label */}
                <Typography variant="label" color="var(--color-text-secondary)" component="p" mb={0.75}>
                  Portfolio name
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g., Tech Stocks, Retirement Fund"
                  value={name}
                  inputProps={{ maxLength: MAX_NAME }}
                  onChange={(e) => { setName(e.target.value); setError(""); }}
                  onKeyPress={handleKeyPress}
                  error={!!error}
                  helperText={
                    <Box
                      component="span"
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      {/* uses theme.typography.helper */}
                      <Typography
                        variant="helper"
                        component="span"
                        color={error ? "var(--color-error)" : "var(--color-text-muted)"}
                      >
                        {error || "Choose a descriptive name for your portfolio"}
                      </Typography>
                      <Typography variant="helper" component="span" color="var(--color-text-secondary)">
                        {name.length}/{MAX_NAME}
                      </Typography>
                    </Box>
                  }
                  InputProps={{ sx: inputSx }}
                  FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
                  autoFocus
                />
              </Box>

              <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                <Button variant="outlined" onClick={onClose} disabled={loading} fullWidth sx={cancelBtnSx}>
                  Cancel
                </Button>
                <Button
                  fullWidth variant="contained"
                  disabled={loading || !isValid}
                  onClick={handleCreate}
                  sx={primaryBtnSx}
                >
                  {loading ? <CircularProgress size={14} color="inherit" /> : "Create portfolio"}
                </Button>
              </Stack>
            </>
          )}

          {/* ════════════════════════════════
              MODE 1 — Import from file
          ════════════════════════════════ */}
          {mode === 1 && (
            <>
              <Typography variant="body" color="var(--color-text-secondary)" component="p" mb={1}>
                Upload an Excel <em>or</em> CSV file with your holdings. Required columns:
              </Typography>

              {/* Column list */}
              <Box sx={{ mb: 1.5, display: "flex", flexDirection: "column", gap: 0.4 }}>
                {["symbol", "quantity", "cost", "date (MM/DD/YYYY, YYYY-MM-DD)"].map((col) => (
                  <Typography key={col} variant="body" color="var(--color-text-secondary)" component="p">
                    • {col}
                  </Typography>
                ))}
              </Box>

              {/* Download template */}
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  component="a"
                  href={market === "TR" ? "/portfolio_template_TR.xlsx" : "/portfolio_template.xlsx"}
                  download
                  size="small"
                  sx={{
                    textTransform: "none",
                    bgcolor: "var(--color-primary)",
                    color: "var(--color-text-inverse)",
                    borderRadius: 1.5,
                    px: 2,
                    "&:hover": { bgcolor: "var(--color-primary-dark)" },
                  }}
                >
                  Download Template
                </Button>
              </Box>

              {/* ── File drop zone ── */}
              <Box>
                <input
                  type="file"
                  accept={ACCEPTED_ACCEPT}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="file-upload"
                />

                <label htmlFor="file-upload">
                  <Button
                    component="span"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      textTransform: "none",
                      py: 3,
                      borderRadius: 1,
                      border: "2px dashed var(--color-border-primary)",
                      bgcolor: "var(--color-bg-secondary)",
                      color: "var(--color-text-secondary)",
                      "&:hover": {
                        bgcolor: "var(--color-surface-hover)",
                        borderColor: "var(--color-primary)",
                        color: "var(--color-text-primary)",
                      },
                    }}
                  >
                    {uploadFile ? uploadFile.name : `Choose file — ${ACCEPTED_LABEL}`}
                  </Button>
                </label>

                {/* File type chips hint */}
                <Box display="flex" gap={0.75} mt={1}>
                  {ACCEPTED_EXTENSIONS.map((ext) => (
                    <Box
                      key={ext}
                      sx={{
                        px: 1, py: 0.25,
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--color-border-primary)",
                        bgcolor: "var(--color-bg-secondary)",
                      }}
                    >
                      <Typography variant="helper" color="var(--color-text-muted)">
                        {ext}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {fileError && (
                  <Box display="flex" alignItems="center" gap={0.75} mt={1.25}>
                    <ErrorIcon sx={{ color: "var(--color-error)" }} />
                    <Typography variant="helper" color="var(--color-error)">
                      {fileError}
                    </Typography>
                  </Box>
                )}
              </Box>

              {marketMismatch && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 1.5,
                    bgcolor: "var(--color-info-light)",
                    color: "var(--color-text-primary)",
                    border: "1px solid var(--color-info)",
                    borderRadius: 1,
                    fontSize: "var(--font-size-sm)",
                    "& .MuiAlert-icon": { color: "var(--color-info)" },
                  }}
                >
                  <Typography
                    fontSize="var(--font-size-sm)"
                    fontWeight="var(--font-weight-semibold)"
                    color="var(--color-text-primary)"
                    mb={0.5}
                  >
                    Portfolio created in a different market
                  </Typography>
                  <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">
                    Your file contained{" "}
                    <strong>{marketMismatch.market_name} ({marketMismatch.market_code})</strong>{" "}
                    stocks, so the portfolio was added to the{" "}
                    <strong>{marketMismatch.market_name}</strong> market — not your current{" "}
                    <strong>{market}</strong> market. Switch to{" "}
                    <strong>{marketMismatch.market_code}</strong> from the market selector to
                    view your new portfolio.
                  </Typography>
                </Alert>
              )}

              {error && (
                <Typography variant="helper" color="var(--color-error)" component="p" mt={1.5}>
                  {error}
                </Typography>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                <Button variant="outlined" onClick={onClose} disabled={uploading} fullWidth sx={cancelBtnSx}>
                  Cancel
                </Button>
                <Button
                  fullWidth variant="contained"
                  disabled={uploading || !uploadFile}
                  onClick={handleImport}
                  startIcon={!uploading && <UploadFileIcon />}
                  sx={primaryBtnSx}
                >
                  {uploading ? <CircularProgress size={14} color="inherit" /> : "Import portfolio"}
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePortfolio;